import React from 'react';
import { cx } from './styles';

/**
 * Empty / error shell. Deliberately supplies NO default text: every call site
 * passes its own existing v1 copy, so no empty-state string can be
 * homogenised away by this refactor.
 */
export interface EmptyStateProps {
  /** Usually an inline SVG. Each page keeps its own distinct icon. */
  icon?: React.ReactNode;
  title: string;
  /** Body copy — string or nodes when the copy contains a link. */
  body?: React.ReactNode;
  /** A single primary affordance, e.g. `Go to Accounts`. */
  action?: React.ReactNode;
  tone?: 'neutral' | 'error';
  className?: string;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  body,
  action,
  tone = 'neutral',
  className,
}) => (
  <div
    className={cx(
      'rounded-2xl border bg-white/[0.02] px-6 py-12 text-center',
      tone === 'error' ? 'border-red-500/20' : 'border-white/[0.06]',
      className,
    )}
  >
    {icon && (
      <div
        className={cx(
          'mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full border',
          tone === 'error'
            ? 'border-red-500/20 bg-red-500/10 text-red-400'
            : 'border-white/[0.08] bg-white/[0.03] text-white/40',
        )}
      >
        {icon}
      </div>
    )}
    <p className={cx('text-base font-semibold', tone === 'error' ? 'text-red-300' : 'text-white')}>
      {title}
    </p>
    {body && <div className="mx-auto mt-2 max-w-md text-sm text-white/50">{body}</div>}
    {action && <div className="mt-6 flex justify-center">{action}</div>}
  </div>
);
